import { X } from 'lucide-react';
import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/cn';

interface BottomSheetContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titleId: string;
  closeOnBackdropClick: boolean;
}

const BottomSheetContext = createContext<BottomSheetContextValue | null>(null);

function useBottomSheetContext() {
  const ctx = useContext(BottomSheetContext);

  if (!ctx) {
    throw new Error('BottomSheet components must be used within BottomSheet');
  }

  return ctx;
}

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  closeOnBackdropClick?: boolean;
}

export function BottomSheet({
  open,
  onOpenChange,
  children,
  closeOnBackdropClick = true,
}: BottomSheetProps) {
  const titleId = useId();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <BottomSheetContext.Provider
      value={{
        open,
        onOpenChange,
        titleId,
        closeOnBackdropClick,
      }}
    >
      {mounted && open ? createPortal(children, document.body) : null}
    </BottomSheetContext.Provider>
  );
}

interface BottomSheetContentProps {
  className?: string;
  children: ReactNode;
  onInteractOutside?: () => void;
}

export function BottomSheetContent({
  className,
  children,
  onInteractOutside,
}: BottomSheetContentProps) {
  const { open, onOpenChange, titleId, closeOnBackdropClick } = useBottomSheetContext();

  const contentRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const dragging = useRef(false);
  const [translateY, setTranslateY] = useState(0);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    const prevOverflow = document.body.style.overflow;

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    setTranslateY(0);
  }, [open]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Dragging is a touch affordance: the desktop panel is a centred card and
    // must not slide around under the mouse.
    if (e.pointerType === 'mouse') return;

    dragging.current = true;
    startY.current = e.clientY;
    currentY.current = 0;

    // Capture on the handle itself — the element that owns the move/up
    // handlers. Capturing on the panel instead would retarget every pointer
    // event to the panel and the handle would never see them.
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;

    const delta = e.clientY - startY.current;

    if (delta > 0) {
      currentY.current = delta;
      setTranslateY(delta);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;

    dragging.current = false;

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    const threshold = (contentRef.current?.offsetHeight ?? 0) * 0.25;

    if (currentY.current > threshold) {
      onOpenChange(false);
    } else {
      setTranslateY(0);
    }

    currentY.current = 0;
  };

  const close = () => onOpenChange(false);

  return (
    <div
      className="fixed inset-x-0 top-0 bottom-13 z-50 flex items-end justify-center md:inset-0 md:items-center md:p-4"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={(e) => {
          e.stopPropagation();

          if (closeOnBackdropClick) {
            close();
          }

          onInteractOutside?.();
        }}
        aria-hidden="true"
      />

      <div
        className={cn(
          // Mobile: a bottom sheet anchored above the tab bar (the wrapper's
          // bottom-13 leaves MobileTabBar visible and tappable). Desktop:
          // a centred card, so no full-width/full-height sheet.
          'bg-surface relative flex max-h-[92dvh] w-full flex-col rounded-t-2xl border border-b-0 border-border shadow-(--shadow-overlay) transition-transform duration-200 will-change-transform',
          'md:max-h-[85dvh] md:max-w-xl md:rounded-2xl md:border-b',
          className,
        )}
        style={{ transform: `translateY(${translateY}px)` }}
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className="flex touch-none justify-center pt-3 md:hidden"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="h-1.5 w-12 cursor-grab rounded-full bg-border/70 active:cursor-grabbing" />
        </div>

        {children}
      </div>
    </div>
  );
}

interface BottomSheetHeaderProps {
  className?: string;
  children: ReactNode;
}

export function BottomSheetHeader({ className, children }: BottomSheetHeaderProps) {
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-3 border-b border-border px-4 py-3',
        className,
      )}
    >
      {children}
    </header>
  );
}

interface BottomSheetTitleProps {
  className?: string;
  children: ReactNode;
}

export function BottomSheetTitle({ className, children }: BottomSheetTitleProps) {
  const { titleId } = useBottomSheetContext();

  return (
    <h2 id={titleId} className={cn('text-base font-semibold', className)}>
      {children}
    </h2>
  );
}

interface BottomSheetCloseProps {
  asChild?: boolean;
  children?: ReactNode;
}

interface CloseChildProps {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  children?: ReactNode;
}

export function BottomSheetClose({ asChild = false, children }: BottomSheetCloseProps) {
  const { onOpenChange } = useBottomSheetContext();

  const handleClick = () => {
    onOpenChange(false);
  };

  if (asChild && isValidElement<CloseChildProps>(children)) {
    const child = children;

    return cloneElement(child, {
      onClick: (event: MouseEvent<HTMLElement>) => {
        child.props.onClick?.(event);

        if (!event.defaultPrevented) {
          handleClick();
        }
      },
    });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Cerrar"
      className="text-ink-muted hover:text-ink rounded p-1 transition-colors"
    >
      {children ?? <X className="size-5" aria-hidden />}
    </button>
  );
}

interface BottomSheetBodyProps {
  className?: string;
  children: ReactNode;
}

export function BottomSheetBody({ className, children }: BottomSheetBodyProps) {
  return <div className={cn('flex-1 overflow-y-auto p-4', className)}>{children}</div>;
}
